/**
 * Профиль: статистика игрока, история ставок и настройки честности.
 */

import { useEffect, useState } from "react";

import {
  NICK_COLORS,
  NICK_STICKERS,
  PREMIUM_PRICE_PAWS,
  VIP_PRICE_PAWS,
  formatPaws,
  formatPlanRemain,
  groupLabel,
  hasPremiumPerks,
  hasVipPerks,
  multiplierToNumber,
  nickColorById,
  nickStickerById,
  type PaidPlan,
  type UserDto,
} from "@koshkodrop/shared";

import { ApiError, api } from "../lib/api";
import { haptic } from "../lib/telegram";
import { useSession } from "../store/session";

interface Bet {
  id: string;
  roundSequence: number;
  stake: string;
  cashedOutAt: number | null;
  payout: string | null;
  status: string;
  crashPoint: number | null;
  createdAt: string;
}

interface Props {
  onOpenAdmin?: () => void;
}

export function ProfilePage({ onOpenAdmin }: Props) {
  const { user, applyUser } = useSession();
  const [bets, setBets] = useState<Bet[]>([]);
  const [clientSeed, setClientSeed] = useState<string>("");
  const [commitHash, setCommitHash] = useState<string>("");
  const [seedDraft, setSeedDraft] = useState("");
  const [seedBusy, setSeedBusy] = useState(false);
  const [previousSeed, setPreviousSeed] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const [betsData, me, fairness] = await Promise.all([
          api.myBets(),
          api.me(),
          api.fairness(),
        ]);
        setBets(betsData.bets);
        setClientSeed(me.clientSeed);
        setCommitHash(fairness.commitHash);
        setSeedDraft(me.clientSeed);
        setPreviousSeed(fairness.previous?.serverSeed ?? null);
      } catch {
        // Профиль и без истории остаётся рабочим
      }
    })();
  }, []);

  const played = bets.length;
  const won = bets.filter((bet) => bet.status === "cashed_out").length;
  const staked = bets.reduce((sum, bet) => sum + BigInt(bet.stake), 0n);
  const returned = bets.reduce((sum, bet) => sum + BigInt(bet.payout ?? "0"), 0n);

  return (
    <div className="space-y-4">
      <section className="flex items-center gap-4 rounded-3xl border border-white/5 bg-night-850/70 p-5">
        {user?.photoUrl ? (
          <img src={user.photoUrl} alt="" className="size-16 rounded-2xl object-cover" />
        ) : (
          <div className="flex size-16 items-center justify-center rounded-2xl bg-night-700 text-3xl">
            😺
          </div>
        )}
        <div className="min-w-0">
          <p className="truncate text-lg font-extrabold text-white">
            {user?.firstName ?? "Гость"}
          </p>
          <p className="tabular truncate text-sm text-white/40">
            ID {user?.telegramId ?? "—"}
          </p>
        </div>
      </section>

      {user && <GroupSection user={user} onUser={applyUser} />}

      {user?.role === "admin" && onOpenAdmin && (
        <button
          type="button"
          onClick={onOpenAdmin}
          className="w-full rounded-2xl border border-paws-500/30 bg-paws-500/10 py-3 text-sm font-extrabold text-paws-300"
        >
          Админ-панель
        </button>
      )}

      <div className="grid grid-cols-2 gap-3">
        <Stat label="Раундов" value={played.toString()} />
        <Stat label="Успешных" value={`${won} из ${played}`} />
        <Stat label="Поставлено" value={formatPaws(staked)} />
        <Stat
          label="Возврат"
          value={formatPaws(returned)}
          tone={returned >= staked ? "win" : "neutral"}
        />
      </div>

      <section className="rounded-3xl border border-white/5 bg-night-850/60 p-4">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-white/40">
          Честная игра
        </h3>
        <p className="mt-2 text-[10px] font-bold uppercase tracking-wide text-white/30">
          Хеш серверного сида
        </p>
        <p className="mt-1 break-all font-mono text-[11px] text-white/50">
          {commitHash || "—"}
        </p>
        <p className="mt-3 text-[10px] font-bold uppercase tracking-wide text-white/30">
          Клиентский сид
        </p>
        <input
          value={seedDraft}
          onChange={(event) => setSeedDraft(event.target.value)}
          className="mt-1 w-full rounded-xl bg-night-900 px-3 py-2 font-mono text-[11px] text-white outline-none"
        />
        <button
          type="button"
          disabled={seedBusy || seedDraft.length < 8}
          onClick={() => {
            void (async () => {
              setSeedBusy(true);
              try {
                const fairness = await api.rotateSeed(seedDraft);
                setClientSeed(fairness.clientSeed);
                setCommitHash(fairness.commitHash);
                setPreviousSeed(fairness.previous?.serverSeed ?? null);
                haptic.success();
              } catch {
                haptic.error();
              } finally {
                setSeedBusy(false);
              }
            })();
          }}
          className="mt-3 w-full rounded-xl bg-paws-500 py-2 text-sm font-extrabold text-night-950 disabled:opacity-35"
        >
          Сменить сид и раскрыть прошлый
        </button>
        {previousSeed && (
          <p className="mt-2 break-all font-mono text-[10px] text-white/35">
            прошлый серверный сид: {previousSeed}
          </p>
        )}
        <p className="mt-2 text-[11px] leading-relaxed text-white/35">
          Хеш публикуется заранее. После смены пары старый серверный сид
          раскрывается — по нему можно пересчитать кейсы, слоты и плинко.
        </p>
      </section>

      <section className="rounded-3xl border border-white/5 bg-night-850/60">
        <h3 className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-white/40">
          Последние ставки
        </h3>
        {bets.length === 0 ? (
          <p className="px-4 pb-4 text-sm text-white/35">Ставок пока не было</p>
        ) : (
          <ul className="divide-y divide-white/5">
            {bets.slice(0, 20).map((bet) => {
              const won = bet.status === "cashed_out";
              return (
                <li key={bet.id} className="flex items-center gap-3 px-4 py-2.5">
                  <span className="tabular w-14 shrink-0 text-xs text-white/35">
                    №{bet.roundSequence}
                  </span>
                  <span className="tabular flex-1 text-sm font-semibold text-white/70">
                    {formatPaws(BigInt(bet.stake))} 🐾
                  </span>
                  <span
                    className={`tabular w-16 text-right text-xs font-bold ${
                      won ? "text-win-400" : "text-lose-400"
                    }`}
                  >
                    {won
                      ? `${multiplierToNumber(bet.cashedOutAt ?? 100).toFixed(2)}x`
                      : bet.crashPoint !== null
                        ? `${multiplierToNumber(bet.crashPoint).toFixed(2)}x`
                        : "—"}
                  </span>
                  <span
                    className={`tabular w-20 text-right text-sm font-bold ${
                      won ? "text-win-400" : "text-white/40"
                    }`}
                  >
                    {won ? `+${formatPaws(BigInt(bet.payout ?? "0"))}` : "—"}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}

function GroupSection({ user, onUser }: { user: UserDto; onUser: (user: UserDto) => void }) {
  const now = useNow(Boolean(user.planUntil));
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<PaidPlan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const vip = hasVipPerks(user.group);
  const premium = hasPremiumPerks(user.group);
  const remain = user.planUntil ? formatPlanRemain(user.planUntil, now) : null;

  const buy = async (plan: PaidPlan) => {
    if (busy) return;
    setBusy(plan);
    setError(null);
    try {
      const result = await api.buyPlan(plan);
      onUser(result.user);
      haptic.success();
    } catch (caught) {
      haptic.error();
      setError(caught instanceof ApiError ? caught.message : "Не удалось купить");
    } finally {
      setBusy(null);
    }
  };

  const saveColor = async (id: string) => {
    try {
      const result = await api.updateCosmetics({ nickColor: id });
      onUser(result.user);
      haptic.select();
    } catch {
      haptic.error();
    }
  };

  const saveSticker = async (id: string | null) => {
    try {
      const result = await api.updateCosmetics({ nickSticker: id });
      onUser(result.user);
      haptic.select();
    } catch {
      haptic.error();
    }
  };

  return (
    <section className="rounded-3xl border border-white/5 bg-night-850/70">
      <button
        type="button"
        onClick={() => {
          haptic.select();
          setOpen((value) => !value);
        }}
        className="flex w-full items-center gap-3 px-4 py-3.5 text-left"
      >
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-bold uppercase tracking-wide text-white/35">Ваша группа</p>
          <p className="mt-0.5 truncate text-base font-extrabold text-white">{groupLabel(user.group)}</p>
          {remain && user.group !== "admin" && user.group !== "user" && (
            <p className="tabular mt-0.5 text-[11px] text-white/40">ещё {remain}</p>
          )}
        </div>
        <GroupBadge group={user.group} />
        <span className={`text-white/35 transition-transform ${open ? "rotate-180" : ""}`}>▾</span>
      </button>

      {open && (
        <div className="space-y-3 border-t border-white/5 px-4 pb-4 pt-3">
          {vip && (
            <div className="space-y-3 rounded-2xl border border-white/8 bg-night-900/80 p-3">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wide text-white/35">Цвет ника</p>
                <div className="mt-2 grid grid-cols-6 gap-1.5">
                  {NICK_COLORS.map((color) => (
                    <button
                      key={color.id}
                      type="button"
                      onClick={() => void saveColor(color.id)}
                      className={`size-8 rounded-full border-2 ${
                        user.nickColor === color.id ? "border-white" : "border-transparent"
                      }`}
                      style={{ background: color.hex }}
                      aria-label={color.label}
                    />
                  ))}
                </div>
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wide text-white/35">
                  Стикер у ника
                </p>
                <div className="mt-2 grid grid-cols-6 gap-1.5">
                  {NICK_STICKERS.map((sticker) => (
                    <button
                      key={sticker.id}
                      type="button"
                      onClick={() =>
                        void saveSticker(sticker.id === user.nickSticker ? null : sticker.id)
                      }
                      className={`rounded-xl py-1.5 text-lg ${
                        user.nickSticker === sticker.id ? "bg-paws-500/25" : "bg-night-850"
                      }`}
                    >
                      {sticker.emoji}
                    </button>
                  ))}
                </div>
              </div>
              <p
                className="truncate text-xs"
                style={{ color: nickColorById(user.nickColor)?.hex ?? "#ffd98a" }}
              >
                {nickStickerById(user.nickSticker)?.emoji ?? ""} {user.firstName}
              </p>
            </div>
          )}

          <PlanOffer
            title="VIP"
            pricePaws={VIP_PRICE_PAWS}
            perks={["Цвет ника в чате", "Стикер рядом с ником"]}
            active={user.group === "vip" || premium}
            busy={busy === "vip"}
            disabled={busy !== null || premium}
            cta={user.group === "vip" ? "Продлить на месяц" : "Купить VIP"}
            onBuy={() => void buy("vip")}
          />
          <PlanOffer
            title="Premium"
            pricePaws={PREMIUM_PRICE_PAWS}
            perks={[
              "Всё из VIP",
              "Мгновенные крутки слотов",
              "До 5 бросков плинко за раз",
              "До 5 кейсов за раз",
              "Авто-участие в краше до 10 раундов",
            ]}
            active={premium}
            busy={busy === "premium"}
            disabled={busy !== null}
            cta={premium && user.group !== "admin" ? "Продлить на месяц" : "Купить Premium"}
            onBuy={() => void buy("premium")}
          />

          {error && <p className="text-center text-xs font-semibold text-lose-300">{error}</p>}
        </div>
      )}
    </section>
  );
}

function GroupBadge({ group }: { group: UserDto["group"] }) {
  const look =
    group === "admin"
      ? { background: "#f5c16c", color: "#12101f" }
      : group === "premium"
        ? { background: "#c084fc", color: "#12101f" }
        : group === "vip"
          ? { background: "#fbbf24", color: "#12101f" }
          : { background: "#1c1a33", color: "rgba(255,255,255,0.55)" };
  return (
    <span className="rounded-full px-3 py-1 text-xs font-extrabold" style={look}>
      {groupLabel(group)}
    </span>
  );
}

function PlanOffer({
  title,
  pricePaws,
  perks,
  active,
  busy,
  disabled,
  cta,
  onBuy,
}: {
  title: string;
  pricePaws: bigint;
  perks: string[];
  active: boolean;
  busy: boolean;
  disabled: boolean;
  cta: string;
  onBuy: () => void;
}) {
  return (
    <div className="rounded-2xl border border-white/8 bg-night-900/70 p-3">
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-sm font-extrabold text-white">{title}</p>
        <p className="tabular text-xs font-bold text-paws-300">
          {formatPaws(pricePaws)} 🐾 / мес
        </p>
      </div>
      <ul className="mt-2 space-y-1">
        {perks.map((perk) => (
          <li key={perk} className="text-[11px] leading-snug text-white/50">
            {perk}
          </li>
        ))}
      </ul>
      <button
        type="button"
        disabled={disabled}
        onClick={onBuy}
        className={`mt-3 w-full rounded-xl py-2.5 text-sm font-extrabold disabled:opacity-35 ${
          active ? "bg-night-800 text-white/70" : "bg-paws-500 text-night-950"
        }`}
      >
        {busy ? "Оплачиваю…" : cta}
      </button>
    </div>
  );
}

function useNow(active: boolean): number {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!active) return;
    const timer = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, [active]);
  return now;
}

function Stat({
  label,
  value,
  tone = "neutral",
}: {
  label: string;
  value: string;
  tone?: "neutral" | "win";
}) {
  return (
    <div className="rounded-2xl border border-white/5 bg-night-850/60 px-4 py-3">
      <p className="text-[10px] font-bold uppercase tracking-wide text-white/30">{label}</p>
      <p
        className={`tabular mt-1 text-lg font-extrabold ${
          tone === "win" ? "text-win-400" : "text-white"
        }`}
      >
        {value}
      </p>
    </div>
  );
}
